// src/components/Report/ReportDetail.jsx
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Alert, Spinner, Button, Card, ListGroup, Badge, Row, Col } from 'react-bootstrap';
import { FiArrowLeft, FiDownloadCloud, FiCalendar, FiAlertTriangle } from 'react-icons/fi';
import api from '../../services/api';
import { getProjectById } from '../../services/projectService';
import { getUserById } from '../../services/action';

const ReportDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const response = await api.get(`/reports/${id}`);
        const reportData = response.data.data;

        // Fetch project details
        let projectDetails = null;
        if (reportData.project) {
          const projectId = reportData.project._id || reportData.project;
          const projectResponse = await getProjectById(projectId);
          projectDetails = projectResponse.data.data; // Adjust based on your API response
        }

        // Fetch user details
        let reportedByDetails = null;
        if (reportData.reportedBy) {
          const userResponse = await getUserById(reportData.reportedBy);
          reportedByDetails = userResponse.data.data; // Adjust based on your API response
        }

        // Merge details into report
        setReport({
          ...reportData,
          project: projectDetails,
          reportedBy: reportedByDetails
        });

      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.response?.data?.message || 'Failed to load report details');
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [id]);


  // Add this debug log
  console.log('Current Report Data:', report);

  const getFieldValue = (fieldName) => {
    return report?.reportData?.[fieldName] || 'Not specified';
  };

  const getProjectField = (fieldName) => {
    return report?.project?.[fieldName] || 'N/A';
  };

  return (
    <div className="container py-4">
      <Button 
        variant="outline-primary" 
        onClick={() => navigate(-1)}
        className="mb-4"
      >
        <FiArrowLeft className="me-2" /> Back
      </Button>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading report details...</p>
        </div>
      ) : error ? (
        <Alert variant="danger">{error}</Alert>
      ) : report ? (
        <div className="report-detail">
          <Card className="mb-4">
            <Card.Header className="d-flex align-items-center bg-light">
              <FiAlertTriangle size={24} className="me-2 text-warning" />
              <h2 className="mb-0">{getFieldValue('title') || 'Untitled Report'}</h2>
            </Card.Header>
            
            <Card.Body>
              <Row>
                <Col md={8}>
                  <div className="mb-4">
                    <h5>Description</h5>
                    <p className="text-muted">
                      {getFieldValue('description') || 'No description provided'}
                    </p>
                  </div>

                  <div className="mb-4">
                    <h5>Report Details</h5>
                    <ListGroup variant="flush">
                      <ListGroup.Item className="d-flex justify-content-between">
                        <span>Report Type:</span>
                        <Badge bg="info" className="text-uppercase">{report.type}</Badge>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between">
                        <span>Status:</span>
                        <Badge 
                          bg={
                            report.status === 'resolved' ? 'success' :
                            report.status === 'under_review' ? 'warning' : 'secondary'
                          }
                        >
                          {report.status.replace(/_/g, ' ')}
                        </Badge>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between">
                        <span>Reported By:</span>
                        <span className="text-muted">
                          {report.reportedBy?.name || 'Unknown'}
                        </span>
                      </ListGroup.Item>
                      <ListGroup.Item className="d-flex justify-content-between">
                        <span>Date Created:</span>
                        <div className="text-muted">
                          <FiCalendar className="me-2" />
                          {new Date(report.createdAt).toLocaleDateString()}
                        </div>
                      </ListGroup.Item>
                    </ListGroup>
                  </div>

                  {/* Type-Specific Details */}
                  {report.type === 'SAFETY' && (
                    <div className="mb-4">
                      <h5>Safety Details</h5>
                      <ListGroup variant="flush">
                        <ListGroup.Item>
                          Hazard Type: {getFieldValue('hazardType')}
                        </ListGroup.Item>
                        <ListGroup.Item>
                          Urgency: {getFieldValue('urgency')}
                        </ListGroup.Item>
                      </ListGroup>
                    </div>
                  )}

                  {report.type === 'DAMAGE' && (
                    <div className="mb-4">
                      <h5>Damage Assessment</h5>
                      <ListGroup variant="flush">
                        <ListGroup.Item>
                          Severity: {getFieldValue('severity')}
                        </ListGroup.Item>
                        <ListGroup.Item>
                          Location: {getFieldValue('location')}
                        </ListGroup.Item>
                      </ListGroup>
                    </div>
                  )}
                </Col>

                <Col md={4}>
                  <Card className="mb-4">
                    <Card.Header>Project Information</Card.Header>
                    <Card.Body>
                      <h6 className="mb-3">{getProjectField('title')}</h6>
                      <p className="text-muted small mb-1">
                        Status: {getProjectField('status')}
                      </p>
                      <p className="text-muted small mb-1">
                        Start Date: {new Date(getProjectField('startDate')).toLocaleDateString()}
                      </p>
                      {report.project?.endDate && (
                        <p className="text-muted small mb-1">
                          End Date: {new Date(getProjectField('endDate')).toLocaleDateString()}
                        </p>
                      )}
                    </Card.Body>
                  </Card>

                  <Card>
                    <Card.Header>Attachments</Card.Header>
                    <Card.Body>
                      {report.files?.length > 0 ? (
                        <ListGroup variant="flush">
                          {report.files.map((file, index) => (
                            <ListGroup.Item key={index} className="d-flex align-items-center">
                              <FiDownloadCloud className="me-2" />
                              <a
                                href={`${api.defaults.baseURL}/${file.path}`}
                                download
                                className="text-decoration-none"
                                target="_blank"
                                rel="noreferrer"
                              >
                                {file.originalname}
                              </a>
                              <small className="text-muted ms-2">
                                ({Math.round(file.size / 1024)} KB)
                              </small>
                            </ListGroup.Item>
                          ))}
                        </ListGroup>
                      ) : (
                        <p className="text-muted mb-0">No files attached</p>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </div>
      ) : (
        <Alert variant="warning">Report not found</Alert>
      )}
    </div>
  );
};

export default ReportDetail;